import { useTranslate, useShow, useInvalidate } from '@refinedev/core';
import { Show, RefreshButton } from '@refinedev/antd';
import { Tag, Tabs, Descriptions, Space } from 'antd';
import type { ContentProductDetailOut } from '@/api/generated/schemas';
import { ProductBulkActions } from '@/components/content_catalog/products/ProductBulkActions';
import { ProductGeneralTab } from '@/components/content_catalog/ProductGeneralTab';
import { ProductContributorsTab } from '@/components/content_catalog/ProductContributorsTab';
import { ProductThemaCodesTab } from '@/components/content_catalog/ProductThemaCodesTab';
import { ProductEbookTab } from '@/components/content_catalog/ProductEbookTab';
import { ProductAudiobookTab } from '@/components/content_catalog/ProductAudiobookTab';
import { ProductManifestTab } from '@/components/content_catalog/ProductManifestTab';
import { ProductMarketingAssetsTab } from '@/components/content_catalog/ProductMarketingAssetsTab';
import { ProductOnixTab } from '@/components/content_catalog/ProductOnixTab';
import { ProductScorecardTab } from '@/components/content_catalog/ProductScorecardTab';
import { productTypeColors } from './constants';

export const ProductShow = () => {
  const t = useTranslate();
  const { query } = useShow<ContentProductDetailOut>({
    resource: 'content_catalog.products',
  });
  const { data, isLoading } = query;
  const record = data?.data;
  const invalidate = useInvalidate();

  const invalidateTranscodingJobs = () =>
    invalidate({
      resource: 'content_catalog.transcoding_jobs',
      invalidates: ['list'],
    });

  const tabItems = [
    {
      key: 'general',
      label: t('content_catalog.products.tabs.general'),
      children: record ? <ProductGeneralTab record={record} /> : null,
    },
    {
      key: 'contributors',
      label: t('content_catalog.products.tabs.contributors'),
      children: (
        <ProductContributorsTab contributors={record?.contributors ?? []} />
      ),
    },
    {
      key: 'thema_codes',
      label: t('content_catalog.products.tabs.thema_codes'),
      children: <ProductThemaCodesTab themaCodes={record?.thema_codes ?? []} />,
    },
  ];

  if (record?.ebook) {
    tabItems.push({
      key: 'ebook',
      label: t('content_catalog.products.tabs.ebook'),
      children: <ProductEbookTab ebook={record.ebook} />,
    });
  }

  if (record?.audiobook) {
    tabItems.push({
      key: 'audiobook',
      label: t('content_catalog.products.tabs.audiobook'),
      children: <ProductAudiobookTab audiobook={record.audiobook} />,
    });
  }

  if (record?.manifest) {
    tabItems.push({
      key: 'manifest',
      label: t('content_catalog.products.tabs.manifest'),
      children: (
        <ProductManifestTab manifest={record.manifest} productId={record.id} />
      ),
    });
  }

  if (record?.marketing_assets && record.marketing_assets.length > 0) {
    tabItems.push({
      key: 'marketing_assets',
      label: t('content_catalog.products.tabs.marketing_assets'),
      children: <ProductMarketingAssetsTab assets={record.marketing_assets} />,
    });
  }

  if (record?.scorecard) {
    tabItems.push({
      key: 'scorecard',
      label: t('content_catalog.products.tabs.scorecard'),
      children: <ProductScorecardTab scorecard={record.scorecard} />,
    });
  }

  if (record?.id) {
    tabItems.push({
      key: 'onix',
      label: t('content_catalog.products.tabs.onix'),
      children: <ProductOnixTab productId={record.id} />,
    });
  }

  return (
    <Show
      isLoading={isLoading}
      headerButtons={({ refreshButtonProps }) => (
        <Space>
          <RefreshButton
            {...refreshButtonProps}
            onClick={(e) => {
              refreshButtonProps?.onClick?.(e);
              invalidateTranscodingJobs();
            }}
          />
          {record && (
            <ProductBulkActions
              ids={[record.id]}
              titles={record.title ? { [record.id]: record.title } : undefined}
              onDone={() => {
                query.refetch();
                invalidateTranscodingJobs();
              }}
            />
          )}
        </Space>
      )}
    >
      {record && (
        <Descriptions
          size="small"
          column={{ xs: 1, sm: 2, md: 4 }}
          style={{ marginBottom: 16 }}
        >
          <Descriptions.Item label={t('base.fields.id')}>
            {record.id}
          </Descriptions.Item>
          <Descriptions.Item
            label={t('content_catalog.products.fields.product_type')}
          >
            {record.product_type ? (
              <Tag color={productTypeColors[record.product_type] || 'default'}>
                {t(
                  `content_catalog.products.product_type.${record.product_type}`
                )}
              </Tag>
            ) : (
              '-'
            )}
          </Descriptions.Item>
          <Descriptions.Item
            label={t('content_catalog.products.fields.isbn')}
            span={2}
          >
            {record.isbn ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item
            label={t('content_catalog.products.fields.title')}
            span={4}
          >
            {record.title ?? '-'}
          </Descriptions.Item>
        </Descriptions>
      )}
      <Tabs defaultActiveKey="general" items={tabItems} />
    </Show>
  );
};
